import {useEffect, useRef, useState, type RefObject} from "react";
import {usePlaybackOverlayCount} from "components/useOverlayActivity";
import {CAMPAIGN_IDS, campaignPaused, CampaignPlayback, focusHoldsCampaign, focusHoldsPlayback, TEXT_ENTRY_FIELDS, type CampaignFrame, type CampaignId} from "./campaignPlayback";
import {halloweenWindow, landingRotation, rotationAfterAnswer, type HalloweenSwitch, type PageLoadEdition, type RotationArrival} from "./halloweenEdition";

/**
 * Browser observations only; selection, elapsed time and pause policy belong to campaignPlayback, and which
 * rotation a visit gets to `halloweenEdition`. `halloween` is the api's switch as `useHalloweenCampaign` reads it, and
 * `arrival` the surface and the query the visit arrived with, which the rotation reads once, as it does the clock;
 * `pageLoad` is the page load's Halloween edition, which every mount of the page load shares.
 */
export function useLandingCampaign(
	visible: boolean, readingArea: RefObject<HTMLDivElement | null>, halloween: HalloweenSwitch | null | undefined, arrival: RotationArrival,
	pageLoad: PageLoadEdition,
) {
	// The prerendered HTML carries this edition, so it is taken from the rotation rather than named:
	// a hardcoded id turns into a silent hydration mismatch the day that edition leaves the rotation.
	const [frame, setFrame] = useState<CampaignFrame>({active: CAMPAIGN_IDS[0], outgoing: null});
	const playback = useRef<CampaignPlayback | null>(null);
	const [hidden, setHidden] = useState(true);
	const [ratio, setRatio] = useState(0);
	const [rotation, setRotation] = useState<readonly CampaignId[] | null>(null);
	const initialized = useRef(false);
	const arrived = useRef(arrival);
	const [reduced, setReduced] = useState(false);
	const [interaction, setInteraction] = useState(false);
	const [fileDrag, setFileDrag] = useState(false);
	const overlays = usePlaybackOverlayCount();
	const paused = campaignPaused({interaction, fileDrag, overlay: overlays > 0, hidden, offscreen: ratio === 0, reduced, covered: !visible});

	useEffect(() => {
		// Identical server/first-client markup. Randomness and the clock are read once per mounted visit, never per render;
		// a Halloween edition once per page load, which `pageLoad` keeps across mounts.
		if (initialized.current) return;
		initialized.current = true;
		const assigned = landingRotation(halloweenWindow(Date.now(), null), Math.random(), arrived.current, pageLoad);
		setRotation(assigned);
		playback.current = new CampaignPlayback(assigned);
		setFrame(playback.current.advance(0, true));
	}, []);

	useEffect(() => {
		// The api's switch, once read, overrides the constant days the visit opened on, both ways; an agreeing
		// answer leaves playback alone (kitecorp/transferify-ui#3776). Which of the two it is is `rotationAfterAnswer`'s rule.
		if (halloween === undefined || rotation === null) return;
		const next = rotationAfterAnswer(rotation, halloweenWindow(Date.now(), halloween), Math.random(), arrived.current, pageLoad);
		if (next === rotation) return;
		setRotation(next);
		playback.current = new CampaignPlayback(next);
		setFrame(playback.current.advance(0, true));
	}, [halloween, rotation, pageLoad]);

	useEffect(() => {
		const media = window.matchMedia("(prefers-reduced-motion: reduce)");
		const syncVisibility = () => setHidden(document.hidden);
		const syncMotion = () => {
			setReduced(media.matches);
			if (media.matches && playback.current) setFrame(playback.current.settleDissolve());
		};
		syncVisibility(); syncMotion();
		document.addEventListener("visibilitychange", syncVisibility);
		media.addEventListener("change", syncMotion);
		const node = readingArea.current;
		// Without a visibility observer, leave the initial ratio at zero and keep the scene resting.
		const observer = typeof IntersectionObserver === "function" ? new IntersectionObserver(entries => {
			if (node === readingArea.current) setRatio(entries[0].intersectionRatio);
		}, {threshold: [0, .5, 1]}) : null;
		if (node) observer?.observe(node);
		return () => {
			document.removeEventListener("visibilitychange", syncVisibility);
			media.removeEventListener("change", syncMotion);
			observer?.disconnect();
		};
	}, [readingArea, frame.active, rotation, visible]);

	useEffect(() => {
		// A resting pointer is not an interaction (owner, 2026-09-06): hovering the drop area or the bar
		// keeps the slides moving. Pressing, visible keyboard focus on a control, file dragging and
		// overlays hold them; which focus counts is {@link focusHoldsPlayback}'s rule, not this hook's.
		// The input starts as the keyboard, as the browser's own `:focus-visible` does before any pointer use.
		let pressed = false, depth = 0, lastInput: "pointer" | "keyboard" = "keyboard";
		const sync = () => {
			const active = document.activeElement;
			setInteraction(pressed || focusHoldsPlayback({
				holdingControl: focusHoldsCampaign(active),
				focusVisible: active?.matches(":focus-visible") === true,
				textEntry: active?.matches(TEXT_ENTRY_FIELDS) === true,
				lastInput,
			}));
		};
		const key = () => { lastInput = "keyboard"; sync(); };
		const down = () => { pressed = true; lastInput = "pointer"; sync(); };
		const up = () => { pressed = false; sync(); };
		const enter = (event: DragEvent) => {
			if (Array.from(event.dataTransfer?.types ?? []).includes("Files")) { depth++; setFileDrag(true); }
		};
		const leave = () => { depth = Math.max(0, depth - 1); if (depth === 0) setFileDrag(false); };
		const drop = () => { depth = 0; setFileDrag(false); };
		const blur = () => { pressed = false; drop(); sync(); };
		document.addEventListener("keydown", key, true);
		document.addEventListener("pointerdown", down, true);
		document.addEventListener("pointerup", up, true);
		document.addEventListener("pointercancel", up, true);
		document.addEventListener("focusin", sync);
		document.addEventListener("focusout", sync);
		window.addEventListener("blur", blur);
		window.addEventListener("dragenter", enter);
		window.addEventListener("dragleave", leave);
		window.addEventListener("drop", drop);
		return () => {
			document.removeEventListener("keydown", key, true);
			document.removeEventListener("pointerdown", down, true);
			document.removeEventListener("pointerup", up, true);
			document.removeEventListener("pointercancel", up, true);
			document.removeEventListener("focusin", sync);
			document.removeEventListener("focusout", sync);
			window.removeEventListener("blur", blur);
			window.removeEventListener("dragenter", enter);
			window.removeEventListener("dragleave", leave);
			window.removeEventListener("drop", drop);
		};
	}, []);

	useEffect(() => {
		if (paused) return;
		let last = performance.now();
		const timer = window.setInterval(() => {
			const now = performance.now();
			const next = playback.current?.advance(now - last, document.hidden);
			last = now;
			if (next) setFrame(next);
		}, 50);
		return () => window.clearInterval(timer);
	}, [paused]);

	return {frame, paused, reduced, ratio, rotation, hidden, obscured: overlays > 0};
}
